import React, { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Upload,
  User,
  Trophy,
  Star,
  FileCheck,
  Loader,
  AlertCircle,
  Download,
  Clock,
  BookOpen,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { useMyNotes, useUserStats, type MyNotesParams } from '../hooks/useApi';
import PageShell from '../components/PageShell';
import { subjectLabels, timeAgo } from '../utils/noteLabels';
import { getNoteAverageRating } from '../utils/noteRatings';

type StatusFilter = NonNullable<MyNotesParams['status']>;

const STATUS_TABS: { id: StatusFilter; label: string }[] = [
  { id: 'all', label: 'Tümü' },
  { id: 'pending', label: 'Onay bekliyor' },
  { id: 'approved', label: 'Yayında' },
];

function levelProgress(points: number, level: number) {
  const currentFloor = Math.max(0, (level - 1) * 100);
  const nextCeiling = level * 100;
  if (nextCeiling <= currentFloor) return 100;
  return Math.min(100, Math.max(0, ((points - currentFloor) / (nextCeiling - currentFloor)) * 100));
}

const UserAccountPage: React.FC = () => {
  usePageTitle('Hesabım');
  const { user, isLoggedIn, openAuthModal } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const statusParam = (searchParams.get('tab') || 'all') as StatusFilter;
  const status: StatusFilter = STATUS_TABS.some((t) => t.id === statusParam) ? statusParam : 'all';

  const { stats, loading: statsLoading } = useUserStats({ enabled: isLoggedIn });
  const { notes, loading: notesLoading, error: notesError, refetch } = useMyNotes({
    status,
    limit: 30,
    enabled: isLoggedIn,
  });

  const progress = useMemo(
    () => (stats ? levelProgress(stats.points, stats.level) : 0),
    [stats]
  );

  const setStatus = (next: StatusFilter) => {
    if (next === 'all') {
      searchParams.delete('tab');
      setSearchParams(searchParams, { replace: true });
    } else {
      setSearchParams({ tab: next }, { replace: true });
    }
  };

  if (!isLoggedIn) {
    return (
      <section
        style={{
          padding: '120px 24px',
          maxWidth: '480px',
          margin: '0 auto',
          textAlign: 'center',
          zIndex: 2,
          position: 'relative',
        }}
      >
        <div className="glow-card surface-card" style={{ padding: '40px 28px' }}>
          <User size={40} color="var(--accent-primary)" style={{ marginBottom: '16px' }} />
          <h1 style={{ margin: '0 0 12px', fontSize: '1.25rem', fontWeight: 800 }}>Hesabınıza giriş yapın</h1>
          <p style={{ color: 'var(--text-muted)', margin: '0 0 20px', fontSize: '14px', lineHeight: 1.6 }}>
            Notlarınızı, puanlarınızı ve katkılarınızı görmek için giriş yapın.
          </p>
          <button type="button" className="btn-primary" onClick={() => openAuthModal('login')} style={{ padding: '12px 24px' }}>
            Giriş yap
          </button>
        </div>
      </section>
    );
  }

  return (
    <PageShell
      badge="Hesabım"
      badgeIcon={User}
      title={`Merhaba, ${user?.name?.split(' ')[0] || 'Üye'}`}
      subtitle="Yüklediğiniz notlar, puanlarınız ve katkılarınız"
      headerExtra={
        <Link to="/notlar/yukle" className="btn-shimmer btn-primary" style={{ padding: '12px 22px', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
          <Upload size={16} />
          Not yükle
        </Link>
      }
    >
      <div className="account-stats-grid">
        <div className="account-stat-card">
          <Trophy size={22} color="var(--accent-primary)" />
          <div>
            <span className="account-stat-card__label">Toplam puan</span>
            <strong>{statsLoading ? '…' : stats?.points ?? 0}</strong>
          </div>
        </div>
        <div className="account-stat-card">
          <Star size={22} color="var(--accent-secondary)" />
          <div>
            <span className="account-stat-card__label">Seviye</span>
            <strong>{statsLoading ? '…' : stats?.level ?? 1}</strong>
          </div>
        </div>
        <div className="account-stat-card">
          <FileCheck size={22} color="var(--accent-warning)" />
          <div>
            <span className="account-stat-card__label">Onaylanan not</span>
            <strong>{statsLoading ? '…' : stats?.notesApproved ?? 0}</strong>
          </div>
        </div>
      </div>

      {!statsLoading && stats && (
        <div className="account-level-bar surface-card" style={{ padding: '18px 20px', marginBottom: '28px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px', color: 'var(--text-muted)' }}>
            <span>Seviye {stats.level}</span>
            <span>{stats.points} puan</span>
          </div>
          <div className="account-level-bar__track">
            <div className="account-level-bar__fill" style={{ width: `${progress}%` }} />
          </div>
        </div>
      )}

      <div className="account-actions" style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '32px' }}>
        <Link to="/notlar/yukle" className="btn-primary" style={{ padding: '12px 22px', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
          <Upload size={16} />
          Yeni not yükle
        </Link>
        <Link to="/notlar" className="btn-ghost" style={{ padding: '12px 22px', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
          <BookOpen size={16} />
          Not arşivi
        </Link>
      </div>

      <div style={{ marginBottom: '16px' }}>
        <h2 style={{ margin: '0 0 4px', fontSize: '1.15rem', fontWeight: 800 }}>Yüklediğim notlar</h2>
        <p style={{ margin: 0, color: 'var(--text-dim)', fontSize: '14px' }}>
          Onay bekleyen ve yayında olan notlarınız
        </p>
      </div>

      <div className="filter-bar" style={{ marginBottom: '20px' }}>
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`filter-chip${status === tab.id ? ' filter-chip--active' : ''}`}
            onClick={() => setStatus(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {notesLoading && (
        <div style={{ textAlign: 'center', padding: '48px', color: 'var(--text-muted)' }}>
          <Loader size={28} style={{ animation: 'spin 1s linear infinite', marginBottom: '12px' }} />
          <p style={{ margin: 0 }}>Notlarınız yükleniyor…</p>
        </div>
      )}

      {notesError && !notesLoading && (
        <div className="surface-card" style={{ padding: '24px', textAlign: 'center', color: 'var(--accent-danger)' }}>
          <AlertCircle size={24} style={{ marginBottom: '8px' }} />
          <p style={{ margin: '0 0 12px' }}>{notesError}</p>
          <button type="button" className="btn-ghost" onClick={() => refetch()} style={{ padding: '8px 16px' }}>
            Tekrar dene
          </button>
        </div>
      )}

      {!notesLoading && !notesError && notes.length === 0 && (
        <div className="surface-card empty-state" style={{ padding: '48px 24px' }}>
          <FileCheck size={36} color="var(--text-dim)" style={{ marginBottom: '12px' }} />
          <p style={{ margin: '0 0 16px' }}>
            {status === 'pending'
              ? 'Onay bekleyen notunuz yok.'
              : status === 'approved'
                ? 'Henüz yayında notunuz yok.'
                : 'Henüz not yüklemediniz.'}
          </p>
          <Link to="/notlar/yukle" className="btn-primary" style={{ padding: '10px 20px', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
            <Upload size={16} />
            İlk notunuzu yükleyin
          </Link>
        </div>
      )}

      {!notesLoading && notes.length > 0 && (
        <div className="account-notes-list">
          {notes.map((note, index) => {
            const avgRating = getNoteAverageRating(note);
            const isPending = note.status === 'pending';
            return (
              <motion.div
                key={note._id}
                className="account-note-row surface-card"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.04 }}
              >
                <div className="account-note-row__main">
                  <h3>{note.title}</h3>
                  <p className="account-note-row__meta">
                    {subjectLabels[note.subject] || note.subject}
                    {' · '}
                    {timeAgo(note.createdAt)}
                  </p>
                  {isPending && (
                    <p className="account-note-row__hint">
                      Admin onayından sonra yayınlanacak.
                    </p>
                  )}
                </div>
                <div className="account-note-row__side">
                  <span className={`account-status-badge account-status-badge--${note.status}`}>
                    {isPending ? (
                      <>
                        <Clock size={12} />
                        Onay bekliyor
                      </>
                    ) : (
                      <>
                        <FileCheck size={12} />
                        Yayında
                      </>
                    )}
                  </span>
                  {!isPending && (
                    <span className="account-note-row__stats">
                      <Download size={13} />
                      {note.downloads}
                      {avgRating > 0 && (
                        <>
                          <Star size={13} fill="var(--accent-warning)" color="var(--accent-warning)" />
                          {avgRating.toFixed(1)}
                        </>
                      )}
                    </span>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </PageShell>
  );
};

export default UserAccountPage;
